import type { CSSProperties } from "react";
import { C, sans, mono, type, space, radius, border, accent } from "../theme.ts";

const QUIET_TONE = { faint: C.inkFaint, ghost: C.inkGhost, soft: C.inkSoft, good: C.good, alert: C.alert } as const;

/**
 * The three buttons these surfaces use. `primary` and `secondary` were one
 * local helper in Detail.tsx plus a hand-rolled duplicate on the login form
 * (15px, 13px padding, full width rather than `flex: 1`) — the two are now
 * one component, which moved the login button to 14px/12px padding to match.
 * `quiet` is the plain-text button used everywhere something is dismissed,
 * copied or closed.
 */
export function Button({
  children, variant = "secondary", tone = "faint", fullWidth, disabled, htmlType = "button", onClick, style,
}: {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "quiet";
  /** `quiet` only — which ink it reads in. */
  tone?: keyof typeof QUIET_TONE;
  fullWidth?: boolean;
  disabled?: boolean;
  htmlType?: "button" | "submit";
  onClick?: () => void;
  style?: CSSProperties;
}) {
  if (variant === "quiet") {
    return (
      <button
        type={htmlType} onClick={onClick} disabled={disabled}
        style={{
          fontFamily: mono, fontSize: type.small, letterSpacing: 1, color: QUIET_TONE[tone],
          background: "none", border: "none", padding: 0, cursor: disabled ? "default" : "pointer",
          ...style,
        }}
      >{children}</button>
    );
  }

  const primary = variant === "primary";
  return (
    <button
      type={htmlType} onClick={onClick} disabled={disabled}
      style={{
        flex: fullWidth ? undefined : primary ? 1 : undefined,
        width: fullWidth ? "100%" : undefined,
        padding: `${space.ml}px ${primary ? 0 : space.xl}px`,
        borderRadius: radius.lg,
        border: primary ? "none" : `${border.hairline}px solid ${C.ruleStrong}`,
        background: primary ? accent : "transparent",
        color: primary ? C.ground : C.ink,
        fontSize: type.bodyLarge, fontFamily: sans, cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
        ...style,
      }}
    >{children}</button>
  );
}
